/**********************************************************
 *                                                        *
 *   SPLICE ARRAY WITH PREFERRED_CENTER_INDEX IN CENTER   *
 *                                                        *
 **********************************************************

- 1.PROBLEM

We have a long ARRAY of items. We have a OUTPUT_SIZE and we want
to show only OUTPUT_SIZE items from ARRAY. 
We also have a PREFERRED_CENTER_INDEX and when we slice our ARRAY
to show only OUTPUT_SIZE items, PREFERRED_CENTER_INDEX needs to be 
positioned in a middle, if possible.

- 2. GOAL

If TOTAL_ITEMS_COUNT <= OUTPUT_SIZE, we return original ARRAY.
If TOTAL_ITEMS_COUNT > OUTPUT_SIZE, we need to find START_INDEX and END_INDEX.
START_INDEX and END_INDEX will be passed to Array.slice.
START_INDEX is inclusive, END_INDEX is exclusive.

- 3. SOLUTION

-- 3.1. END_INDEX

END_INDEX is easy - it’s just START_INDEX plus OUTPUT_SIZE.
That way, no matter the PREFERRED_CENTER_INDEX position,
the final length of the array will always be OUTPUT_SIZE.
For it to work, we have to ensure that the START_INDEX is never
bigger than TOTAL_ITEMS_COUNT - OUTPUT_SIZE. We will get to that part later.

-- 3.2. START_INDEX

--- 3.2.1. START_INDEX_MIN

First, we want to ensure that START_INDEX will never
be LESSER than 0, so we take BIGGER out of two numbers:
0 and START_OFFSET = PREFERRED_CENTER_INDEX - HALF_OF_OUTPUT_SIZE.
HALF_OF_OUTPUT_SIZE is used to ensure that the PREFERRED_CENTER_INDEX
will be centered if there is possibility of centering it.
→ START_INDEX_MIN = Math.max(0, START_OFFSET)

--- 3.2.2. START_INDEX_MAX

Now as we have the lower limit, we can work on upper limit.
We want to ensure that START_INDEX will never
be BIGGER than TOTAL_ITEMS_COUNT - OUTPUT_SIZE.
→ START_INDEX_MAX = TOTAL_ITEMS_COUNT - OUTPUT_SIZE

--- 3.2.3. START_INDEX - final value

Now as we have both START_INDEX_MIN and START_INDEX_MAX, we have
to find the LOWER number between those two.
→ START_INDEX = Math.min(START_INDEX_MIN, START_INDEX_MAX)

- 4. FINAL ALGORITHM

  • START_OFFSET = PREFERRED_CENTER_INDEX - HALF_OF_OUTPUT_SIZE
  • START_INDEX_MIN = Math.max(0, START_OFFSET)
  • START_INDEX_MAX = TOTAL_ITEMS_COUNT - OUTPUT_SIZE
  • START_INDEX = Math.min(START_INDEX_MIN, START_INDEX_MAX)
  • END_INDEX = START_INDEX + OUTPUT_SIZE

- 5. EXAMPLES

Sample array:

  • ARRAY = [0,1,2,3,4,5,6]
  • TOTAL_ITEMS_COUNT = 7
  • OUTPUT_SIZE = 5
  • HALF_OF_OUTPUT_SIZE = FLOOR( OUTPUT_SIZE / 2 ) = 2

-- 5.1. EXAMPLE #1 - PREFERRED_CENTER_INDEX = 1

  • START_OFFSET = PREFERRED_CENTER_INDEX - HALF_OF_OUTPUT_SIZE = 1 - 2 = -1
  • START_INDEX_MIN = Math.max(0, START_OFFSET) = Math.max(0, -1) = 0
  • START_INDEX_MAX = TOTAL_ITEMS_COUNT - OUTPUT_SIZE = 7 - 5 = 2
  • START_INDEX = Math.min(0, 2) = 0
  • END_INDEX = 0 + OUTPUT_SIZE = 5
Resulting array - [0,1,2,3,4]

-- 5.2. EXAMPLE #2 - PREFERRED_CENTER_INDEX = 3

  • START_OFFSET = PREFERRED_CENTER_INDEX - HALF_OF_OUTPUT_SIZE = 3 - 2 = 1
  • START_INDEX_MIN = Math.max(0, START_OFFSET) = Math.max(0, 1) = 1
  • START_INDEX_MAX = TOTAL_ITEMS_COUNT - OUTPUT_SIZE = 7 - 5 = 2
  • START_INDEX = Math.min(1, 2) = 1
  • END_INDEX = 1 + OUTPUT_SIZE = 1 + 5 = 6
Resulting array - [1,2,3,4,5]

-- 5.3. EXAMPLE #3 - PREFERRED_CENTER_INDEX = 6

  • START_OFFSET = PREFERRED_CENTER_INDEX - HALF_OF_OUTPUT_SIZE = 6 - 2 = 4
  • START_INDEX_MIN = Math.max(0, START_OFFSET) = Math.max(0, 4) = 4
  • START_INDEX_MAX = TOTAL_ITEMS_COUNT - OUTPUT_SIZE = 7 - 5 = 2
  • START_INDEX = Math.min(4, 2) = 2
  • END_INDEX = 2 + OUTPUT_SIZE = 2 + 5 = 7
Resulting array - [2,3,4,5,6]

- 6. SUMMARY

This algorithm ensures that with any ARRAY, TOTAL_ITEMS_COUNT, OUTPUT_SIZE
and PREFERRED_CENTER_INDEX, the resulting array will always be of
length TOTAL_ITEMS_COUNT, always show the PREFERRED_CENTER_INDEX, 
and the PREFERRED_CENTER_INDEX - if possible - will always be
positioned in the middle.
*/

/**
 * @param ARRAY Array of items to slice
 * @param PREFERRED_CENTER_INDEX Index of an item that should be positioned in the middle
 * @param OUTPUT_SIZE Size of the output array after the slice
 * @returns Sliced array of items with the active item in the middle
 */
export const getCenteredSlice = <T>(
  ARRAY: T[],
  PREFERRED_CENTER_INDEX: number,
  OUTPUT_SIZE: number
): { items: T[]; itemsBefore: T[]; itemsAfter: T[] } => {
  const TOTAL_ITEMS_COUNT = ARRAY.length

  // No need to slice ARRAY if it's smaller than OUTPUT_SIZE
  if (TOTAL_ITEMS_COUNT <= OUTPUT_SIZE) {
    return { items: ARRAY, itemsBefore: [], itemsAfter: [] }
  }

  // If PREFERRED_CENTER_INDEX is outside array's bounds, change it to 0
  const CENTER_INDEX =
    PREFERRED_CENTER_INDEX < 0 || PREFERRED_CENTER_INDEX > TOTAL_ITEMS_COUNT - 1
      ? 0
      : PREFERRED_CENTER_INDEX

  const HALF_OF_OUTPUT_SIZE = Math.floor(OUTPUT_SIZE / 2)
  const START_OFFSET = CENTER_INDEX - HALF_OF_OUTPUT_SIZE

  const START_INDEX_MIN = Math.max(0, START_OFFSET)
  const START_INDEX_MAX = TOTAL_ITEMS_COUNT - OUTPUT_SIZE

  const SLICE_START = Math.min(START_INDEX_MIN, START_INDEX_MAX)
  const SLICE_END = SLICE_START + OUTPUT_SIZE

  const centeredItems = ARRAY.slice(SLICE_START, SLICE_END)
  const itemsBefore = ARRAY.slice(0, SLICE_START)
  const itemsAfter = ARRAY.slice(SLICE_END)

  return { items: centeredItems, itemsBefore, itemsAfter }
}
